import { parseISO, isSameDay } from 'date-fns';

/**
 * Validates if a delivery date is still valid based on cut-off time
 * @param {string} deliveryDate - Date string in format "YYYY-MM-DD"
 * @param {Array} dateList - Array of date options from PICKUP_DATE_LIST
 * @returns {Object} { isValid: boolean, message: string, dateOption: object }
 */
export const validateDeliveryCutOffTime = (deliveryDate, dateList = []) => {
    if (!deliveryDate || !dateList || dateList.length === 0) {
        return {
            isValid: false,
            message: 'Please select a delivery date.',
            dateOption: null
        };
    }

    // Find the selected date option from the list
    const selectedDateOption = dateList.find(d => d.date === deliveryDate);

    if (!selectedDateOption) {
        return {
            isValid: false,
            message: 'Selected delivery date not found in available dates',
            dateOption: null
        };
    }

    try {
        const parsedDeliveryDate = parseISO(selectedDateOption.date);
        const today = new Date();

        // If delivery date is not today, it's valid (assuming it's a future date)
        if (!isSameDay(parsedDeliveryDate, today)) {
            // Check if it's a past date
            if (parsedDeliveryDate < today) {
                return {
                    isValid: false,
                    message: 'Selected delivery date is in the past',
                    dateOption: selectedDateOption
                };
            }
            return {
                isValid: true,
                message: 'Delivery date is valid',
                dateOption: selectedDateOption
            };
        }

        // If delivery date is today, check cut_off_time
        if (selectedDateOption.cut_off_time) {
            const now = new Date();
            const [hours, minutes, seconds] = selectedDateOption.cut_off_time.split(':').map(Number);
            const cutOffTime = new Date();
            cutOffTime.setHours(hours, minutes, seconds || 0, 0);

            // If current time exceeds cut_off_time, it's invalid
            if (now >= cutOffTime) {
                return {
                    isValid: false,
                    message: `The cut-off time for ${selectedDateOption.label} has passed. Please select a different delivery date.`,
                    dateOption: selectedDateOption
                };
            }
        }

        return {
            isValid: true,
            message: 'Delivery date is valid',
            dateOption: selectedDateOption
        };
    } catch (error) {
        console.error('Error validating delivery date:', error);
        return {
            isValid: false,
            message: 'Error validating delivery date',
            dateOption: selectedDateOption
        };
    }
};

/**
 * Filters delivery dates based on current time and cut-off times
 * @param {Array} dateList - Array of date options from PICKUP_DATE_LIST
 * @returns {Array} Filtered array of valid date options
 */
export const filterValidDeliveryDates = (dateList = []) => {
    if (!dateList || dateList.length === 0) {
        return [];
    }

    const now = new Date();

    return dateList.filter((dateOption) => {
        try {
            const deliveryDate = parseISO(dateOption.date);
            const today = new Date();

            // If delivery date is not today, include it
            if (!isSameDay(deliveryDate, today)) {
                // Only show future dates
                return deliveryDate > today;
            }

            // If delivery date is today, check cut_off_time
            if (dateOption.cut_off_time) {
                const [hours, minutes, seconds] = dateOption.cut_off_time.split(':').map(Number);
                const cutOffTime = new Date();
                cutOffTime.setHours(hours, minutes, seconds || 0, 0);

                // Show only if current time is before cut_off_time
                return now < cutOffTime;
            }

            // If no cut_off_time specified, include the date
            return true;
        } catch (error) {
            console.error('Error parsing date:', error);
            return false;
        }
    });
};
